import { useCallback, useEffect, useRef, useState } from 'react';
import WorkModalShell from '@/components/common/WorkModalShell';

/** An explicit choice per output job; pending jobs resolve to Cancel on unmount. */
export function usePrintPrivacy(enabled = true) {
  const [open, setOpen] = useState(false);
  const pending = useRef<((masked: boolean | null) => void) | null>(null);
  const trigger = useRef<HTMLElement | null>(null);
  const mounted = useRef(true);
  const available = useRef(enabled);
  available.current = enabled;
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      pending.current?.(null);
      pending.current = null;
    };
  }, []);
  useEffect(() => {
    if (!enabled) {
      pending.current?.(null);
      pending.current = null;
      setOpen(false);
    }
  }, [enabled]);
  const askPrivacy = useCallback(
    () =>
      new Promise<boolean | null>((resolve) => {
        if (!mounted.current || !available.current || pending.current) return resolve(null);
        trigger.current =
          document.activeElement instanceof HTMLElement ? document.activeElement : null;
        pending.current = resolve;
        setOpen(true);
      }),
    [],
  );
  const settle = (choice: boolean | null) => {
    const resolve = pending.current;
    pending.current = null;
    setOpen(false);
    requestAnimationFrame(() => {
      if (!mounted.current || !available.current) return resolve?.(null);
      trigger.current?.focus();
      resolve?.(choice);
    });
  };
  return {
    askPrivacy,
    privacyDialog: open ? (
      <WorkModalShell open title="Mask customer mobile numbers?" onClose={() => settle(null)}>
        <h2 className="mt-3 text-lg font-semibold">Mask customer mobile numbers?</h2>
        <p className="my-3 text-sm text-muted-foreground">
          Masking shows only the last four digits in this copy. Saved contacts and the business
          number stay unchanged.
        </p>
        <div className="grid gap-2">
          <button
            type="button"
            onClick={() => settle(true)}
            className="min-h-11 rounded-xl bg-primary px-4 font-semibold text-primary-foreground"
          >
            Yes, mask numbers
          </button>
          <button
            type="button"
            onClick={() => settle(false)}
            className="min-h-11 rounded-xl border border-border px-4"
          >
            No, show full numbers
          </button>
          <button
            type="button"
            onClick={() => settle(null)}
            className="min-h-11 rounded-xl px-4 text-muted-foreground"
          >
            Cancel
          </button>
        </div>
      </WorkModalShell>
    ) : null,
  };
}
